/**
 * Mahjong solitaire board. Positions are in tile-unit space — each tile
 * occupies a 1×1 footprint, so a horizontally-touching tile sits at dx=±1,
 * a vertically-touching tile at dy=±1. Half-unit offsets are allowed for
 * layouts like Turtle's dragon body that stagger.
 *
 * Stacking: `layer` 0 sits on the table; layer N sits on top of overlapping
 * layer N-1 tiles. The render layer Y position is just `layer * STACK_LIFT`.
 *
 * Freeness (the central invariant): a tile is "free" — eligible to be picked —
 * iff (a) no tile overlaps it on the layer above, AND (b) at least one of its
 * left or right sides has no abutting same-layer tile.
 */

import type { Tile } from './tile.js';

export interface BoardTile {
  id: string;     // tile id
  x: number;      // grid center, x axis (right = +)
  y: number;      // grid center, y axis (table forward = +)
  layer: number;  // 0 = table, 1 = on top of layer 0, …
}

export interface Board {
  tiles: Map<string, BoardTile>;
  removed: string[];
}

const TILE_W = 1;
const TILE_H = 1;
const EPS = 1e-6;

function xyOverlap(a: BoardTile, b: BoardTile): boolean {
  return Math.abs(a.x - b.x) < TILE_W - EPS && Math.abs(a.y - b.y) < TILE_H - EPS;
}

function yOverlap(a: BoardTile, b: BoardTile): boolean {
  return Math.abs(a.y - b.y) < TILE_H - EPS;
}

export function cloneBoard(board: Board): Board {
  return {
    tiles: new Map(Array.from(board.tiles, ([id, t]) => [id, { ...t }])),
    removed: board.removed.slice(),
  };
}

export function buildBoard(positions: ReadonlyArray<Omit<BoardTile, 'id'>>, tiles: readonly Tile[]): Board {
  if (positions.length !== tiles.length) {
    throw new Error(`board mismatch: ${positions.length} positions vs ${tiles.length} tiles`);
  }
  const map = new Map<string, BoardTile>();
  positions.forEach((p, i) => {
    map.set(tiles[i]!.id, { id: tiles[i]!.id, ...p });
  });
  return { tiles: map, removed: [] };
}

function isCovered(board: Board, t: BoardTile): boolean {
  for (const o of board.tiles.values()) {
    if (o.id === t.id) continue;
    if (o.layer !== t.layer + 1) continue;
    if (xyOverlap(o, t)) return true;
  }
  return false;
}

/** Returns true if any same-layer tile is touching `t` on the requested side. */
function isSideBlocked(board: Board, t: BoardTile, side: -1 | 1): boolean {
  for (const o of board.tiles.values()) {
    if (o.id === t.id || o.layer !== t.layer) continue;
    if (!yOverlap(o, t)) continue;
    const dx = o.x - t.x;
    if (side === -1 && dx < -EPS && dx > -TILE_W - EPS) return true;
    if (side === 1 && dx > EPS && dx < TILE_W + EPS) return true;
  }
  return false;
}

export function isFree(board: Board, tileId: string): boolean {
  const t = board.tiles.get(tileId);
  if (!t) return false;
  if (isCovered(board, t)) return false;
  return !isSideBlocked(board, t, -1) || !isSideBlocked(board, t, 1);
}

export function freeTileIds(board: Board): string[] {
  const out: string[] = [];
  for (const id of board.tiles.keys()) {
    if (isFree(board, id)) out.push(id);
  }
  return out;
}

export function removePair(board: Board, a: string, b: string): Board {
  const next = cloneBoard(board);
  next.tiles.delete(a);
  next.tiles.delete(b);
  next.removed.push(a, b);
  return next;
}
